var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box, IconButton, Typography } from '@mui/material';
import React from 'react';
import { themeColors } from '../JuvareTheme';
import { useCommonWebTranslation } from '../hooks';
import { TreeItemSelectionButtons } from './components';
import { areAllTreeItemsSelected, expandTreeItemsToShowSelectedItems, flattenTreeItems } from './utils';
/**
 * Renders a tree structure UI with support for keyboard navigation, selection, focus, and expansion.
 * It uses a flattened view of tree items for efficient rendering and interaction.
 *
 * @template T - The type extending TreeItem representing the structure of each node in the tree.
 *
 * @param {object} props - Component props.
 * @param {T[]} props.items - Array of root-level tree items to be rendered.
 * @param {string[]} props.expandedItemIds - Array of currently expanded item IDs.
 * @param {(expandedItemIds: string[] | undefined) => void} props.onChangeExpandedItemIds - Callback to update expanded item IDs.
 * @param {string} props.activeItemId - ID of the currently active item.
 * @param {(activeItemId?: string) => void} props.onChangeActiveItemId - Callback to update the active item ID.
 * @param {string} props.focusItemId - ID of the currently focused item.
 * @param {(focusItemId: string) => void} props.onChangeFocusItemId - Callback to update the focused item ID.
 * @param {string[]} props.selectedItemIds - Array of currently selected item IDs.
 * @param {(selectedItemIds: string[]) => void} props.onChangeSelectedItemIds - Callback to update selected item IDs.
 * @param {(itemId: string[]) => void} props.onSelectAllAsync - Async callback to select all children.
 * @param {(itemId: string[]) => void} props.onDeselectAllAsync - Async callback to deselect all children.
 * @param {boolean} props.itemSelectionEnabled - Enables checkbox selection on items.
 * @param {boolean} props.border - Enables visual border styling for tree items.
 * @param {'small' | 'medium' | 'large'} props.size - Size of the tree item UI.
 * @param {string[]} props.highlightedItemIds - IDs of items to visually highlight.
 * @param {React.FC<TreeItemContentProps<T>>} props.itemContentComponent - Optional custom component to render each tree item’s content.
 * @param {(item: T) => string} props.getItemLabel - Function to extract label string from a tree item.
 * @param {TreeItemContentProps<T>['customProps']} props.customItemContentComponentProps - Props passed to the custom item content component.
 * @param {string} props.'data-test' - Optional test ID.
 *
 * @returns {React.ReactElement} Rendered TreeView component.
 */
export function TreeView(_a) {
    var { items } = _a, props = __rest(_a, ["items"]);
    const [preventFocusEvent, setPreventFocusEvent] = React.useState(false);
    const ref = React.useRef(null);
    const flatTreeItems = React.useMemo(() => { var _a; return flattenTreeItems(items, (_a = props.expandedItemIds) !== null && _a !== void 0 ? _a : []); }, [items, props.expandedItemIds]);
    React.useEffect(() => {
        if (props.activeItemId && flatTreeItems.findIndex((item) => item.item.id === props.activeItemId) === -1) {
            props.onChangeExpandedItemIds((prev) => {
                if (props.activeItemId) {
                    const expandedIds = expandTreeItemsToShowSelectedItems(items, [props.activeItemId]);
                    if (expandedIds.length > 0) {
                        const next = [...(prev !== null && prev !== void 0 ? prev : [])];
                        for (const expandedId of expandedIds) {
                            if (!next.includes(expandedId)) {
                                next.push(expandedId);
                            }
                        }
                        return next;
                    }
                }
                return prev;
            });
        }
    });
    const handleRowClick = () => setPreventFocusEvent(false);
    const handleFocus = () => {
        if (!preventFocusEvent && props.focusItemId == null && flatTreeItems.length > 0) {
            const newFocusItemId = flatTreeItems[0].item.id;
            props.onChangeFocusItemId(newFocusItemId);
            scrollIntoViewIfNeeded(ref, newFocusItemId);
        }
    };
    const handleKeyDown = (event) => {
        var _a;
        if (event.target !== ref.current || flatTreeItems.length === 0) {
            return;
        }
        const expandedItemIds = (_a = props.expandedItemIds) !== null && _a !== void 0 ? _a : [];
        const focusItemIndex = flatTreeItems.findIndex((i) => i.item.id === props.focusItemId);
        let focusItemId;
        if (focusItemIndex !== -1) {
            if (!flatTreeItems[focusItemIndex].item.disabled) {
                focusItemId = flatTreeItems[focusItemIndex].item.id;
            }
        }
        let newFocusItemId;
        switch (event.key) {
            case 'ArrowUp':
                event.preventDefault();
                if (focusItemIndex === -1 || focusItemIndex === 0) {
                    newFocusItemId = flatTreeItems[flatTreeItems.length - 1].item.id;
                }
                else {
                    newFocusItemId = flatTreeItems[focusItemIndex - 1].item.id;
                }
                props.onChangeFocusItemId(newFocusItemId);
                scrollIntoViewIfNeeded(ref, newFocusItemId);
                break;
            case 'ArrowDown':
                event.preventDefault();
                if (focusItemIndex === -1 || focusItemIndex === flatTreeItems.length - 1) {
                    newFocusItemId = flatTreeItems[0].item.id;
                }
                else {
                    newFocusItemId = flatTreeItems[focusItemIndex + 1].item.id;
                }
                props.onChangeFocusItemId(newFocusItemId);
                scrollIntoViewIfNeeded(ref, newFocusItemId);
                break;
            case 'ArrowLeft':
                event.preventDefault();
                if (focusItemId) {
                    const expanded = expandedItemIds.includes(focusItemId);
                    if (expanded) {
                        props.onChangeExpandedItemIds(expandedItemIds.filter((id) => id !== focusItemId));
                    }
                }
                break;
            case 'ArrowRight':
                event.preventDefault();
                if (focusItemId) {
                    const expanded = expandedItemIds.includes(focusItemId);
                    if (!expanded) {
                        props.onChangeExpandedItemIds([...expandedItemIds, focusItemId]);
                    }
                }
                break;
            case 'Home':
                event.preventDefault();
                newFocusItemId = flatTreeItems[0].item.id;
                if (focusItemId !== newFocusItemId) {
                    props.onChangeFocusItemId(newFocusItemId);
                    scrollIntoViewIfNeeded(ref, newFocusItemId);
                }
                break;
            case 'End':
                event.preventDefault();
                newFocusItemId = flatTreeItems[flatTreeItems.length - 1].item.id;
                if (focusItemId !== newFocusItemId) {
                    props.onChangeFocusItemId(newFocusItemId);
                    scrollIntoViewIfNeeded(ref, newFocusItemId);
                }
                break;
            case 'Enter':
                event.preventDefault();
                if (focusItemId) {
                    props.onChangeActiveItemId(focusItemId);
                }
                break;
        }
    };
    return (React.createElement(Box, { ref: ref, role: 'treegrid', "aria-multiselectable": props.itemSelectionEnabled, "aria-readonly": true, tabIndex: 0, onKeyDown: handleKeyDown, onFocus: handleFocus, 
        // when clicking on any tree row and its elements, we must prevent 'focus' event from updating
        // state for focusItemId, otherwise TreeView will be re-rendered before the click event handler is executed,
        // and it will be lost it after re-rendering. 'click' event updates focusItemId state as well alongside
        // activeItemId, so this does not have any unwanted effects.
        onMouseDown: () => setPreventFocusEvent(true), sx: {
            outline: 'none',
            backgroundColor: themeColors.surface.primary,
            '&:focus-within .tree-item-focus .tree-item-content': { borderColor: themeColors.border.main },
            '&:focus-within .tree-item-focus .tree-item-border': { borderColor: themeColors.border.main },
            '&:focus .tree-item-focus .tree-item-content': { borderColor: themeColors.primary.border },
            '&:focus .tree-item-focus .tree-item-border': { borderColor: themeColors.primary.border },
            '& .tree-item-focus:focus-within .tree-item-hover-content': { display: 'block' },
            '& .tree-item-content:hover .tree-item-hover-content': { display: 'block' },
        }, "data-test": props['data-test'] }, flatTreeItems.map(({ item, level }, index) => {
        var _a, _b, _c, _d;
        return (React.createElement(TreeViewRowMemo, { key: item.id, item: item, size: (_a = props.size) !== null && _a !== void 0 ? _a : 'medium', border: !!props.border, first: index === 0, last: index === flatTreeItems.length - 1, level: level, focus: item.id === props.focusItemId, active: item.id === props.activeItemId, expanded: item.children != null && item.children.length > 0 && !!((_b = props.expandedItemIds) === null || _b === void 0 ? void 0 : _b.includes(item.id)), selected: !!((_c = props.selectedItemIds) === null || _c === void 0 ? void 0 : _c.includes(item.id)), allSelected: item.children != null && areAllTreeItemsSelected(item.children, props.selectedItemIds), itemSelectionEnabled: props.itemSelectionEnabled, onChangeSelectedItemIds: props.onChangeSelectedItemIds, onSelectAllAsync: props.onSelectAllAsync, onDeselectAllAsync: props.onDeselectAllAsync, onRowClick: handleRowClick, highlighted: !!((_d = props.highlightedItemIds) === null || _d === void 0 ? void 0 : _d.includes(item.id)), customProps: props.customItemContentComponentProps, parentRef: ref, onChangeFocusItemId: props.onChangeFocusItemId, onChangeActiveItemId: props.onChangeActiveItemId, onChangeExpandedItemIds: props.onChangeExpandedItemIds, itemContentComponent: props.itemContentComponent, getItemLabel: props.getItemLabel }));
    })));
}
const styleConstantsBySize = {
    small: {
        itemGap: 4,
        itemPadding: 1,
        itemBorderWidth: 1,
        iconButtonSize: 34,
        levelIndentWidth: 34 - 1,
    },
    medium: {
        itemGap: 8,
        itemPadding: 3,
        itemBorderWidth: 1,
        iconButtonSize: 40,
        levelIndentWidth: 40 - 3,
    },
    large: {
        itemGap: 8,
        itemPadding: 3,
        itemBorderWidth: 1,
        iconButtonSize: 48,
        levelIndentWidth: 48 - 3,
    },
};
export const TreeViewRowMemo = React.memo(TreeViewRow);
/**
 * Renders a single row in the TreeView, including label, expand/collapse button, selection controls, and optional custom content.
 *
 * @template T - The type of the TreeItem being rendered.
 *
 * @param {object} props - Props object.
 * @param {T} props.item - The tree item to render.
 * @param {'small' | 'medium' | 'large'} props.size - Size of the row (affects spacing and icon sizing).
 * @param {boolean} props.border - Whether to display top/bottom borders.
 * @param {boolean} props.first - Whether this is the first visible tree item (affects top border).
 * @param {boolean} props.last - Whether this is the last visible tree item (affects bottom border).
 * @param {number} props.level - The depth of the item in the tree hierarchy.
 * @param {boolean} props.focus - Whether this item currently has focus.
 * @param {boolean} props.active - Whether this item is currently active.
 * @param {boolean} props.expanded - Whether this item’s children are currently visible.
 * @param {boolean} props.selected - Whether this item is selected.
 * @param {boolean} props.allSelected - Whether all children of this item are selected.
 * @param {boolean} props.itemSelectionEnabled - Whether selection checkboxes are shown.
 * @param {(selectedItemIds: string[]) => void} [props.onChangeSelectedItemIds] - Callback for updating selected items.
 * @param {() => void} props.onRowClick - Callback for when the row is clicked.
 * @param {boolean} props.highlighted - Whether this item is visually highlighted.
 * @param {TreeItemContentProps<T>['customProps']} [props.customProps] - Additional custom props to pass to the item content renderer.
 * @param {React.RefObject<HTMLDivElement>} props.parentRef - Ref to the scrollable parent element.
 * @param {(focusItemId: string) => void} props.onChangeFocusItemId - Callback to update the focused item.
 * @param {(activeItemId?: string) => void} props.onChangeActiveItemId - Callback to update the active item.
 * @param {(expandedItemIds: string[] | undefined) => void} props.onChangeExpandedItemIds - Callback to expand/collapse item.
 * @param {(itemIds: string[]) => void} [props.onSelectAllAsync] - Callback to select all children asynchronously.
 * @param {(itemIds: string[]) => void} [props.onDeselectAllAsync] - Callback to deselect all children asynchronously.
 * @param {React.FC<TreeItemContentProps<T>>} [props.itemContentComponent] - Optional custom component to render the item label area.
 * @param {(item: T) => string} [props.getItemLabel] - Function to return a string label from a tree item.
 *
 * @returns {React.ReactElement} The rendered TreeView row element.
 */
function TreeViewRow({ item, size, border, first, last, level, focus, active, expanded, selected, allSelected, itemSelectionEnabled, onChangeSelectedItemIds, onRowClick, highlighted, customProps, parentRef, onChangeFocusItemId, onChangeActiveItemId, onChangeExpandedItemIds, onSelectAllAsync, onDeselectAllAsync, itemContentComponent, getItemLabel, }) {
    var _a;
    const { t } = useCommonWebTranslation();
    const { itemGap, itemPadding, itemBorderWidth, iconButtonSize, levelIndentWidth } = styleConstantsBySize[size];
    const hasChildren = item.children != null && item.children.length > 0;
    const ItemContentComponent = itemContentComponent;
    const itemContentComponentProps = {
        item: item,
        size: size,
        border: border,
        first: first,
        last: last,
        level: level,
        focus: focus,
        active: active,
        expanded: expanded,
        selected: selected,
        allSelected: allSelected,
        itemSelectionEnabled: itemSelectionEnabled,
        onChangeSelectedItemIds: onChangeSelectedItemIds,
        onSelectAllAsync,
        onDeselectAllAsync,
        highlighted: highlighted,
        customProps: customProps,
    };
    return (React.createElement(React.Fragment, null,
        React.createElement(Box, { role: 'row', "aria-expanded": hasChildren ? expanded : undefined, "aria-selected": itemSelectionEnabled ? selected : active, "data-item-id": item.id, sx: { position: 'relative', mt: '-1px', '&:first-of-type': { mt: 0 }, zIndex: focus ? 1 : undefined }, className: focus ? 'tree-item-focus' : undefined },
            React.createElement(Box, { role: 'gridcell', "aria-selected": itemSelectionEnabled ? selected : active, sx: {
                    minHeight: iconButtonSize + itemPadding * 2,
                    display: 'flex',
                    alignItems: 'center',
                    gap: `${itemGap}px`,
                    pl: `${level * levelIndentWidth + itemPadding - itemBorderWidth}px`,
                    pr: `${itemPadding - itemBorderWidth}px`,
                    pt: `${itemPadding - itemBorderWidth}px`,
                    pb: `${itemPadding - itemBorderWidth}px`,
                    border: `${itemBorderWidth}px solid transparent`,
                    backgroundColor: active ? themeColors.surface.pressed : highlighted ? themeColors.surface.hover : undefined,
                    cursor: 'pointer',
                    '&:hover': {
                        backgroundColor: active ? themeColors.surface.pressed : themeColors.surface.hover,
                    },
                }, className: 'tree-item-content', onClick: () => {
                    onChangeFocusItemId(item.id);
                    onChangeActiveItemId(!item.disabled ? item.id : undefined);
                    scrollIntoViewIfNeeded(parentRef, item.id);
                    onRowClick();
                } },
                hasChildren ? (React.createElement(Box, { sx: { width: iconButtonSize, flexShrink: 0 } }, expanded ? (React.createElement(IconButton, { size: size, tabIndex: focus ? 0 : -1, onClick: (event) => {
                        event.stopPropagation();
                        onChangeExpandedItemIds((prev) => (prev !== null && prev !== void 0 ? prev : []).filter((id) => id !== item.id));
                    }, "aria-label": t('common.collapse'), "data-test": 'collapse-button' },
                    React.createElement(ExpandLessIcon, null))) : (React.createElement(IconButton, { size: size, tabIndex: focus ? 0 : -1, onClick: (event) => {
                        event.stopPropagation();
                        onChangeExpandedItemIds((prev) => [...(prev !== null && prev !== void 0 ? prev : []), item.id]);
                    }, "aria-label": t('common.expand'), "data-test": 'expand-button' },
                    React.createElement(ExpandMoreIcon, null))))) : (React.createElement(Box, { sx: { width: iconButtonSize, flexShrink: 0 } })),
                React.createElement(Box, { sx: { flexGrow: 1, minWidth: 0 } }, ItemContentComponent ? (React.createElement(ItemContentComponent, Object.assign({}, itemContentComponentProps))) : (React.createElement(Box, { sx: {
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        alignItems: { xs: 'stretch', sm: 'center' },
                        gap: 1,
                    } },
                    React.createElement(Typography, { variant: size === 'large' ? 'body1' : 'body2', component: 'div', sx: {
                            flexGrow: 1,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            pt: { xs: 1, sm: 0 },
                        }, "data-test": 'label' }, (_a = getItemLabel === null || getItemLabel === void 0 ? void 0 : getItemLabel(item)) !== null && _a !== void 0 ? _a : ''),
                    itemSelectionEnabled && !item.disabled && (React.createElement(Box, { sx: {
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            alignSelf: { xs: 'end', sm: 'center' },
                            pr: { xs: 1, sm: 0 },
                        } },
                        React.createElement(TreeItemSelectionButtons, Object.assign({}, itemContentComponentProps)))))))),
            border && (React.createElement(React.Fragment, null,
                first && (React.createElement(Box, { sx: {
                        position: 'absolute',
                        left: 0,
                        right: 0,
                        top: 0,
                        borderBottom: `1px solid ${themeColors.border.main}`,
                        pointerEvents: 'none',
                    }, className: 'tree-item-border' })),
                React.createElement(Box, { sx: {
                        position: 'absolute',
                        left: last || level === 0 ? 0 : level * levelIndentWidth + itemPadding + iconButtonSize + itemGap,
                        right: 0,
                        bottom: 0,
                        borderBottom: `1px solid ${themeColors.border.main}`,
                        pointerEvents: 'none',
                    }, className: 'tree-item-border' }))))));
}
/**
 * Ensures that a specific tree item is scrolled into view, if not already visible.
 *
 * @param {React.RefObject<HTMLDivElement | null>} parentRef - Ref to the container element holding the tree rows.
 * @param {string} itemId - The ID of the tree item to scroll into view.
 *
 * @returns {void}
 */
function scrollIntoViewIfNeeded(parentRef, itemId) {
    var _a, _b;
    (_b = (_a = parentRef.current) === null || _a === void 0 ? void 0 : _a.querySelector(`[data-item-id='${CSS.escape(itemId)}']`)) === null || _b === void 0 ? void 0 : _b.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
}
